import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Shield,
  FileText,
  Cookie,
  Sliders,
  AlertTriangle,
  Eye,
  Database,
  CheckCircle,
  Lock,
  Mail,
  Users,
  HelpCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  MapPin,
  Scale,
  Award,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import './Legal.css';

export type PolicySection =
  | 'terms'
  | 'privacy'
  | 'community'
  | 'safety'
  | 'age-policy'
  | 'location-privacy'
  | 'grievance'
  | 'copyright'
  | 'user-content'
  | 'no-payment'
  | 'cookies'
  | 'preferences'
  | 'disclaimer'
  | 'accessibility'
  | 'dpa'
  | 'acceptable-use'
  | 'security'
  | 'disclosure'
  | 'help';

interface NavDoc {
  id: PolicySection;
  title: string;
  icon: any;
  category: 'Core Terms & Privacy' | 'Community & Safety' | 'Regulatory & Grievance' | 'Compliance & Standards';
}

const DOCS: NavDoc[] = [
  { id: 'terms', title: 'Terms of Service', icon: FileText, category: 'Core Terms & Privacy' },
  { id: 'privacy', title: 'Privacy Notice (DPDP)', icon: Shield, category: 'Core Terms & Privacy' },
  { id: 'location-privacy', title: 'Location Privacy Notice', icon: MapPin, category: 'Core Terms & Privacy' },
  { id: 'user-content', title: 'User Content Notice', icon: FileText, category: 'Core Terms & Privacy' },
  { id: 'community', title: 'Community & Acceptable Use', icon: Users, category: 'Community & Safety' },
  { id: 'safety', title: 'Safety Guidelines', icon: ShieldAlert, category: 'Community & Safety' },
  { id: 'age-policy', title: 'Age Limitation (18+)', icon: ShieldAlert, category: 'Community & Safety' },
  { id: 'no-payment', title: 'No-Payment Notice', icon: ShieldCheck, category: 'Community & Safety' },
  { id: 'grievance', title: 'Grievance Redressal Policy', icon: Scale, category: 'Regulatory & Grievance' },
  { id: 'copyright', title: 'Copyright & IP Policy', icon: Award, category: 'Regulatory & Grievance' },
  { id: 'dpa', title: 'Data Processing Agreement', icon: Database, category: 'Regulatory & Grievance' },
  { id: 'security', title: 'Security Architecture', icon: Lock, category: 'Compliance & Standards' },
  { id: 'cookies', title: 'Cookie Policy', icon: Cookie, category: 'Compliance & Standards' },
  { id: 'preferences', title: 'Cookie Preferences', icon: Sliders, category: 'Compliance & Standards' },
  { id: 'disclaimer', title: 'Platform Disclaimer', icon: AlertTriangle, category: 'Compliance & Standards' },
  { id: 'accessibility', title: 'Accessibility Statement', icon: Eye, category: 'Compliance & Standards' },
  { id: 'help', title: 'Help Center & Support', icon: HelpCircle, category: 'Compliance & Standards' },
];

export default function LegalPage({ defaultDoc }: { defaultDoc?: PolicySection }) {
  const location = useLocation();
  const navigate = useNavigate();

  const getDocFromPath = (): PolicySection => {
    if (defaultDoc) return defaultDoc;
    const p = location.pathname.replace(/^\//, '').toLowerCase();
    if (p === 'terms' || p === 'terms-of-service' || p === 'legal') return 'terms';
    if (p === 'privacy' || p === 'privacy-policy') return 'privacy';
    if (p === 'community' || p === 'community-guidelines') return 'community';
    if (p === 'safety') return 'safety';
    if (p === 'age-policy' || p === 'age-limit' || p === 'minors' || p === 'minor-policy') return 'age-policy';
    if (p === 'location-privacy') return 'location-privacy';
    if (p === 'grievance' || p === 'grievances') return 'grievance';
    if (p === 'copyright' || p === 'ip') return 'copyright';
    if (p === 'user-content') return 'user-content';
    if (p === 'no-payment') return 'no-payment';
    if (p === 'cookies' || p === 'cookie-policy') return 'cookies';
    if (p === 'cookie-preferences' || p === 'preferences') return 'preferences';
    if (p === 'disclaimer') return 'disclaimer';
    if (p === 'accessibility' || p === 'accessibility-statement') return 'accessibility';
    if (p === 'dpa' || p === 'data-processing') return 'dpa';
    if (p === 'security' || p === 'security-policy') return 'security';
    if (p === 'help' || p === 'support') return 'help';
    return 'terms';
  };

  const [activeDoc, setActiveDoc] = useState<PolicySection>(getDocFromPath());

  useEffect(() => {
    setActiveDoc(getDocFromPath());
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname, defaultDoc]);

  // Cookie preferences local state
  const [cookieSettings, setCookieSettings] = useState({
    essential: true,
    performance: true,
    functional: true,
    matchmaking: true,
  });
  const [savedStatus, setSavedStatus] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('kheldrop_cookie_settings');
      if (stored) {
        try {
          setCookieSettings(JSON.parse(stored));
        } catch (e) {
          // ignore
        }
      }
    }
  }, []);

  const handleSavePreferences = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('kheldrop_cookie_settings', JSON.stringify(cookieSettings));
      setSavedStatus(true);
      setTimeout(() => setSavedStatus(false), 2500);
    }
  };

  const categories = Array.from(new Set(DOCS.map((d) => d.category)));

  const currentDocMeta = DOCS.find((d) => d.id === activeDoc);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0e', color: '#e2e2ec', fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
      <SEOHead
        title={currentDocMeta ? `${currentDocMeta.title} | Legal & Trust` : 'Legal & Trust Hub'}
        description={currentDocMeta?.description || 'Review KhelDrop legal policies, terms of service, player privacy, location safety, and community guidelines.'}
        canonical={`/${activeDoc}`}
      />
      {/* Top Navigation Bar */}
      <header className="legal-header">
        <div className="legal-header-inner">
          <div className="legal-header-left">
            <Link to="/" className="legal-back-btn">
              <ArrowLeft size={14} /> Back to Radar
            </Link>
            <div className="legal-brand">
              <div className="legal-brand-badge">P</div>
              <span className="legal-brand-title">
                KHELDROP LEGAL & REGULATORY HUB
              </span>
            </div>
          </div>

          <div className="legal-header-right">
            <Link to="/settings" className="legal-privacy-btn">
              Privacy Center (My Data)
            </Link>
          </div>
        </div>
      </header>

      {/* Main Layout Container */}
      <div className="legal-layout-container">
        {/* Mobile Document Selector (Visible only on mobile/tablet <= 860px) */}
        <div className="legal-mobile-nav">
          <label htmlFor="legal-doc-picker" className="legal-mobile-nav-label">
            Active Legal Document:
          </label>
          <div className="legal-mobile-select-wrap">
            <select
              id="legal-doc-picker"
              value={activeDoc}
              onChange={(e) => {
                const nextId = e.target.value as PolicySection;
                setActiveDoc(nextId);
                navigate(`/${nextId === 'terms' ? 'terms' : nextId}`, { replace: true });
              }}
              className="legal-mobile-select"
            >
              {categories.map((cat) => (
                <optgroup key={cat} label={cat}>
                  {DOCS.filter((d) => d.category === cat).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.title}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <ChevronDown size={16} className="legal-mobile-chevron" />
          </div>
        </div>

        {/* Desktop Sidebar Navigation (Hidden on <= 860px) */}
        <aside className="legal-sidebar">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {categories.map((cat) => (
              <div key={cat}>
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: '#71717a',
                    marginBottom: '8px',
                    paddingLeft: '10px',
                  }}
                >
                  {cat}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {DOCS.filter((d) => d.category === cat).map((d) => {
                    const Icon = d.icon;
                    const isActive = activeDoc === d.id;
                    return (
                      <button
                        key={d.id}
                        onClick={() => {
                          setActiveDoc(d.id);
                          navigate(`/${d.id === 'terms' ? 'terms' : d.id}`, { replace: true });
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: isActive ? '1px solid rgba(139, 92, 246, 0.4)' : '1px solid transparent',
                          background: isActive ? 'rgba(139, 92, 246, 0.12)' : 'transparent',
                          color: isActive ? '#c4b5fd' : '#a1a1aa',
                          fontWeight: isActive ? 700 : 500,
                          fontSize: '13px',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <Icon size={15} color={isActive ? '#a78bfa' : '#71717a'} />
                        <span style={{ flex: 1 }}>{d.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* Policy Document Content Area */}
        <main className="legal-main-content">
          {activeDoc === 'terms' && <TermsOfServiceDoc />}
          {activeDoc === 'privacy' && <PrivacyNoticeDoc />}
          {activeDoc === 'location-privacy' && <LocationPrivacyDoc />}
          {(activeDoc === 'community' || activeDoc === 'acceptable-use') && <CommunityGuidelinesDoc />}
          {activeDoc === 'safety' && <SafetyGuidelinesDoc />}
          {activeDoc === 'age-policy' && <AgeLimitationPolicyDoc />}
          {activeDoc === 'no-payment' && <NoPaymentDoc />}
          {activeDoc === 'grievance' && <GrievancePolicyDoc />}
          {activeDoc === 'copyright' && <CopyrightPolicyDoc />}
          {activeDoc === 'user-content' && <UserContentDoc />}
          {activeDoc === 'dpa' && <DpaDoc />}
          {(activeDoc === 'security' || activeDoc === 'disclosure') && <SecurityDoc />}
          {activeDoc === 'cookies' && <CookiePolicyDoc />}
          {activeDoc === 'preferences' && (
            <CookiePreferencesDoc
              settings={cookieSettings}
              onToggle={(k: any) => setCookieSettings((prev: any) => ({ ...prev, [k]: !prev[k] }))}
              onSave={handleSavePreferences}
              savedStatus={savedStatus}
            />
          )}
          {activeDoc === 'disclaimer' && <DisclaimerDoc />}
          {activeDoc === 'accessibility' && <AccessibilityDoc />}
          {activeDoc === 'help' && <HelpDoc />}
        </main>
      </div>

      <Footer />
    </div>
  );
}

/* =========================================================================================
   DOCUMENT 1: KHELDROP TERMS OF SERVICE (MATURE STRATIFIED PLATFORM STRUCTURE)
   ========================================================================================= */
function TermsOfServiceDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#a78bfa', letterSpacing: '0.06em' }}>
          Intermediary Safe Harbor & User Agreement
        </span>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px', letterSpacing: '-0.02em' }}>
          KhelDrop Terms of Service
        </h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>
          Effective Date: 5 October 2026 | Last Updated: 5 October 2026 | Version 2026.1
        </div>
      </div>

      <div style={{ background: 'rgba(139, 92, 246, 0.08)', border: '1px solid rgba(139, 92, 246, 0.25)', padding: '16px 20px', borderRadius: '12px', marginBottom: '28px' }}>
        <p style={{ margin: 0, fontSize: '13px', color: '#c4b5fd', fontWeight: 500 }}>
          <strong>Preamble:</strong> These Terms of Service ("Terms") govern your access to and use of the KhelDrop website, application, software and related services ("KhelDrop", "Platform", "we", "us", or "our") operated by <strong>KhelDrop Technologies Private Limited</strong> (and its founding proprietors/lawful operators pending issuance of statutory corporate certificates), having its principal operational office at Plot 42, Cyber Hub, DLF Phase 2, Gurugram, NCR Delhi – 122002, India.
        </p>
        <p style={{ margin: '8px 0 0', fontSize: '12.5px', color: '#a1a1aa' }}>
          By accessing, registering for, or using KhelDrop, you agree to these Terms and the policies incorporated into them, including the KhelDrop Privacy Notice, Community Guidelines, Safety Guidelines, Location Privacy Notice and applicable grievance and intellectual-property policies. If you do not agree to these Terms, do not access or use KhelDrop.
        </p>
      </div>

      <h3>1. Nature of KhelDrop</h3>
      <p>KhelDrop is a technology platform designed to help users discover, create, organise and participate in recreational sporting activities and games.</p>
      <p>KhelDrop may enable users to: discover games and sporting activities; create activities; join activities organised by other users; communicate with other users; use location-based functionality; check in to sporting activities; maintain profiles; receive or display participation or reliability information.</p>
      <p><strong>KhelDrop is a technology platform.</strong> Unless expressly stated otherwise for a particular service, KhelDrop is not: the organiser of every game listed on the Platform; the owner or operator of every venue; a sports coach; a referee; a medical provider; an emergency service; a guardian; a security or background-check provider; the agent or representative of any user; responsible for the acts or omissions of independent users or third parties.</p>

      <h3>2. Eligibility</h3>
      <p>KhelDrop is intended for users who are at least 18 years of age unless KhelDrop expressly states otherwise and implements the safeguards required by applicable law.</p>
      <p>You represent that: the information you provide is accurate and current; you have authority to enter into these Terms; you will comply with applicable law; you will not create an account through fraudulent or misleading information. Where KhelDrop adopts an 18+ policy, users must not circumvent age restrictions.</p>

      <h3>3. Accounts</h3>
      <p>You are responsible for maintaining the security of your account credentials and for notifying KhelDrop promptly if you suspect unauthorised access. You must not: share your account for another person's use; impersonate another person; create fraudulent accounts; use compromised credentials; attempt to gain unauthorised access to another account.</p>
      <p>KhelDrop may suspend or terminate an account where reasonably necessary to protect users, the Platform, investigate abuse, enforce these Terms or comply with law.</p>

      <h3>4. User-Organised Games</h3>
      <p>Users may create and organise games or sporting activities. Unless expressly stated otherwise, the user creating or organising a game is responsible for: the accuracy of the game information supplied; the chosen venue; communicating participation requirements; informing participants of known activity-specific requirements; complying with venue rules; conducting the activity lawfully.</p>
      <p>KhelDrop does not guarantee that: a game will occur; a game will have a minimum number of participants; any participant will attend; the venue will remain available; another participant will behave as represented; any user possesses a particular skill level; information supplied by users is complete or accurate.</p>

      <h3>5. Physical Activity Risks</h3>
      <p>Participation in sporting and recreational activities involves inherent risks, including falls, collisions, physical injury, illness, dehydration, equipment failure, environmental conditions, venue hazards and acts or omissions of other participants.</p>
      <p>You voluntarily decide whether to participate and are responsible for assessing your own ability, health, equipment and circumstances. KhelDrop does not provide medical advice, medical supervision, emergency response or professional sporting supervision. Users should not participate where they reasonably believe they are medically or physically unable to do so safely. <em>Nothing in these Terms excludes or limits liability that cannot lawfully be excluded.</em></p>

      <h3>6. User-to-User Interactions</h3>
      <p>KhelDrop provides a means for users to discover and interact with other users. KhelDrop does not ordinarily represent or warrant that a user: has provided accurate identity information; is trustworthy; possesses stated qualifications; is medically fit; has a particular sporting ability; has no criminal history; will behave lawfully or safely.</p>
      <p>You are responsible for exercising independent judgment when interacting with other users. KhelDrop is not a party to private arrangements between users unless expressly stated otherwise.</p>

      <h3>7. Venue Disclaimer</h3>
      <p>Third-party venues may be listed or referenced on KhelDrop. Unless explicitly stated otherwise, KhelDrop does not own, operate, inspect, certify or guarantee the condition, safety, legality, availability or quality of any third-party venue.</p>
      <p>You should independently verify venue: availability; operating hours; access requirements; safety conditions; rules; equipment; permissions; suitability for your activity.</p>

      <h3>8. Location Services</h3>
      <p>KhelDrop may use location information to provide location-based functionality, including nearby-game discovery, distance calculation and check-in functionality. Location data may be inaccurate because of device limitations, GPS availability, network conditions, map data, incorrect user information or third-party services.</p>
      <p>KhelDrop does not warrant that location, distance, geofencing, map or venue information is accurate or continuously available. Location information must not be relied upon for emergency, medical, security-critical or life-safety decisions. You are responsible for controlling device/browser permissions and using location functions responsibly.</p>

      <h3>9. User Content</h3>
      <p>Users remain responsible for content they submit, post, transmit or otherwise make available through KhelDrop ("User Content"). You represent that you have the necessary rights and permissions to submit the User Content.</p>
      <p>You must not submit content that: infringes another person's rights; violates privacy; is unlawful; is fraudulent; is abusive or harassing; contains malicious code; impersonates another person; unlawfully exposes another person's personal information; violates the Community Guidelines.</p>
      <p>You grant KhelDrop a non-exclusive, worldwide, royalty-free licence to host, store, reproduce, transmit, format and display your User Content only to the extent reasonably necessary to operate, secure, maintain and provide KhelDrop.</p>

      <h3>10. Content Moderation</h3>
      <p>To operate a safe and lawful Platform, KhelDrop may: review reports; restrict content; remove content; disable access to content; suspend accounts; terminate accounts; cancel activities; preserve records; cooperate with lawful authorities. KhelDrop does not guarantee that every unlawful or harmful item will be identified before being viewed.</p>

      <h3>11. Acceptable Use</h3>
      <p>You must use KhelDrop lawfully. You must not use KhelDrop to: stalk, threaten or harass others; obtain or distribute another person's private information without authority; commit fraud; impersonate another person; introduce malware; attack or disrupt Platform infrastructure; scrape data in violation of these Terms or applicable law; manipulate reputation or participation information; create fraudulent games; facilitate criminal activity; facilitate gambling, betting or prohibited money gaming; publish unlawful or infringing material.</p>

      <h3>12. No Payments</h3>
      <p>As of the Effective Date, KhelDrop does not provide payment processing, wallets, paid game entry, subscriptions, betting, wagering or monetary prize functionality. Users must not use KhelDrop to arrange prohibited payments, wagers or money-game activity. Private financial arrangements outside KhelDrop are between the relevant parties and are not transactions with KhelDrop. This section must be updated before any payment or monetisation feature is launched.</p>

      <h3>13. Third-Party Services</h3>
      <p>KhelDrop may depend on third-party services, including authentication, hosting, databases, mapping/geocoding, communication, analytics and infrastructure providers. KhelDrop does not control third-party services and does not guarantee their continuous availability, accuracy, security or performance. Third-party services may be subject to their own terms and privacy policies.</p>

      <h3>14. Platform Availability</h3>
      <p>KhelDrop is provided on an "as available" basis. We do not guarantee that the Platform will always be: available; uninterrupted; secure; error-free; compatible with every device; free from malicious activity; accurate; permanently available. Access may be interrupted by maintenance, technical failures, network failures, third-party failures, cyber incidents, legal requirements or events beyond our reasonable control.</p>

      <h3>15. No False Claims</h3>
      <p>KhelDrop does not represent that: every user has been background-checked; every user has been identity-verified; every venue has been inspected; every game is supervised by KhelDrop; every sporting activity is safe; location data is always accurate; every user-provided statement is true; the Platform is free from all security vulnerabilities. Where KhelDrop provides a verification badge or other verification feature, the scope of the verification will be stated separately. A verification feature is not a guarantee of a person's behaviour, safety, competence or suitability.</p>

      <h3>16. Disclaimers</h3>
      <p>To the maximum extent permitted by applicable law, KhelDrop disclaims warranties not expressly required by law, including warranties regarding availability, suitability, accuracy, reliability, uninterrupted operation and fitness for a particular purpose. KhelDrop does not guarantee the actions, conduct, safety, qualifications, identity or suitability of independent users or third parties.</p>

      <h3>17. Limitation of Liability</h3>
      <p>To the maximum extent permitted by applicable law, KhelDrop and its officers, directors, employees, contractors, agents and service providers shall not be liable for indirect, incidental, special, exemplary or consequential loss or damage, including loss of profits, goodwill, business opportunity, anticipated savings or data, arising from or connected with: use of or inability to use the Platform; user-to-user interactions; user-organised sporting activities; venue conditions; third-party services; inaccurate user-provided information; GPS, map or geolocation inaccuracies; telecommunications failures; internet failures; unauthorised acts of third parties; content created or supplied by users; circumstances beyond KhelDrop's reasonable control.</p>
      <p><strong>Liability Cap:</strong> KhelDrop's aggregate liability for claims arising from your use of the Platform shall, to the maximum extent permitted by applicable law, be limited to <strong>INR 100 (One Hundred Indian Rupees)</strong> or the aggregate fees paid by you to KhelDrop in the preceding 30 days (which, for zero-cost recreational games, is INR 0).</p>
      <p><em>No limitation or exclusion in these Terms applies to the extent such limitation or exclusion is prohibited by applicable law. Nothing in these Terms removes any mandatory statutory right, remedy or liability that cannot legally be excluded.</em></p>

      <h3>18. Personal Data and Security Incidents</h3>
      <p>KhelDrop will process personal data in accordance with its Privacy Notice and applicable law. KhelDrop will implement reasonable technical and organisational safeguards appropriate to the Platform and applicable legal requirements. No internet-connected system can be guaranteed to be completely secure.</p>
      <p>To the maximum extent permitted by applicable law, KhelDrop shall not be responsible for losses resulting solely from unauthorised acts, attacks, failures or conduct of third parties that were outside KhelDrop's reasonable control, provided that this provision does not remove any statutory obligation that cannot lawfully be excluded. Nothing in this section waives or limits KhelDrop's statutory obligations concerning personal-data processing or breach response.</p>

      <h3>19. Indemnity</h3>
      <p>To the maximum extent permitted by law, you agree to indemnify KhelDrop and its officers, directors, employees and contractors against claims, losses, damages, liabilities and reasonable legal costs arising from: your breach of these Terms; your unlawful conduct; User Content supplied by you; infringement caused by your User Content; misuse of another person's personal data; your interactions with other users; your participation in physical activities; your violation of applicable law. This indemnity does not apply to the extent a loss is finally determined to have resulted from KhelDrop's own non-excludable liability.</p>

      <h3>20. Suspension and Termination</h3>
      <p>KhelDrop may suspend, restrict or terminate access where reasonably necessary to: prevent fraud; protect users; protect Platform security; enforce the Terms; investigate abuse; comply with law; respond to lawful government or court directions.</p>

      <h3>21. Intellectual Property</h3>
      <p>KhelDrop's software, trademarks, branding, design and original materials are owned by or licensed to KhelDrop unless otherwise stated. No ownership rights are transferred to you through your use of the Platform.</p>

      <h3>22. Grievances</h3>
      <p>
        Grievances may be submitted to:<br />
        <strong>Grievance Officer:</strong> Ashutosh Yadav<br />
        <strong>Email:</strong> grievance@kheldrop.app<br />
        <strong>Address:</strong> KhelDrop Legal & Regulatory Office, Barakhamba Road, Connaught Place, New Delhi – 110001, India<br />
        KhelDrop will process grievances according to applicable law and its Grievance Redressal Policy.
      </p>

      <h3>23. Governing Law and Dispute Resolution</h3>
      <p>These Terms are governed by the laws of India, subject to mandatory applicable law. Courts at <strong>New Delhi, India</strong> shall have exclusive jurisdiction, subject to applicable statutory jurisdiction and remedies. Nothing in these Terms prevents a person from exercising a statutory right before a forum or authority that has mandatory jurisdiction.</p>

      <h3>24. Severability</h3>
      <p>If any provision is declared invalid or unenforceable, it shall be modified or severed to the minimum extent necessary and the remaining provisions shall continue.</p>

      <h3>25. Changes to Terms</h3>
      <p>KhelDrop may update these Terms when the Platform, law, security requirements or business model changes. The current version will be published on KhelDrop.</p>

      <h3>26. Contact</h3>
      <p>
        <strong>Legal Entity / Data Fiduciary:</strong> KhelDrop Technologies Private Limited (or its lawful operating entity)<br />
        <strong>Operational Address:</strong> Plot 42, Cyber Hub, DLF Phase 2, Gurugram, NCR Delhi – 122002, India<br />
        <strong>Legal Email:</strong> legal@kheldrop.app<br />
        <strong>Support Email:</strong> support@kheldrop.app
      </p>
    </article>
  );
}

/* =========================================================================================
   DOCUMENT 2: STATUTORY DPDP PRIVACY NOTICE (RULE 3 COMPLIANT)
   ========================================================================================= */
function PrivacyNoticeDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#10b981', letterSpacing: '0.06em' }}>
          DPDP Act, 2023 & DPDP Rules, 2025 Notice
        </span>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px', letterSpacing: '-0.02em' }}>
          KhelDrop Privacy Notice
        </h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>
          Effective Date: 5 October 2026 | Last Updated: 5 October 2026 | Version 2026.1
        </div>
      </div>

      <div style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.25)', padding: '16px 20px', borderRadius: '12px', marginBottom: '28px' }}>
        <p style={{ margin: 0, fontSize: '13px', color: '#6ee7b7', fontWeight: 500 }}>
          This Privacy Notice explains how <strong>KhelDrop Technologies Private Limited</strong> (and its lawful operating entity), operating KhelDrop ("KhelDrop", "we", "us", "our"), processes digital personal data in strict compliance with India's <strong>Digital Personal Data Protection Act, 2023</strong> and the <strong>DPDP Rules, 2025</strong>.
        </p>
      </div>

      <h3>1. Our Role</h3>
      <p>For processing activities where KhelDrop determines the purpose and means of processing personal data, KhelDrop acts as the relevant <strong>Data Fiduciary</strong> under applicable Indian data-protection law.</p>

      <h3>2. Itemised Personal Data Inventory (Rule 3 Compliance)</h3>
      <div style={{ overflowX: 'auto', margin: '20px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <thead>
            <tr style={{ background: 'rgba(255, 255, 255, 0.05)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Data Category</th>
              <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Specific Purpose of Processing</th>
              <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Source</th>
              <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Lawful Basis</th>
              <th style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Retention Horizon</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}><strong>Full Name</strong></td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Account creation, match roster identification</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>User / Google OAuth</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Consent (DPDP §6)</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Duration of active account</td>
            </tr>
            <tr>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}><strong>Email Address</strong></td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Authentication, critical security alerts, DPO notices</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>User / Google OAuth</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Consent / Contract</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Active account + 1 yr security logs</td>
            </tr>
            <tr>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}><strong>Single-Event GPS</strong></td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>200m venue geofence check-in verification</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Device GPS sensor</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Specific Event Consent</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Ephemeral: released upon validation</td>
            </tr>
            <tr>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}><strong>Venue Coordinates</strong></td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Public match discovery, distance radar</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Game Creator / Host</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Service functionality</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Archived after game completion</td>
            </tr>
            <tr>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}><strong>Reliability Score</strong></td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Community anti-ditch and attendance tracking</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Derived from check-ins</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Community Standard</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Rolling 90-day activity window</td>
            </tr>
            <tr>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}><strong>Game Messages</strong></td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Match logistics among registered players</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>User chat input</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Consent / Communication</td>
              <td style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>Purged 30 days after match</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>3. Notice and Affirmative Consent</h3>
      <p>Where consent is the basis for processing, KhelDrop provides a clear, understandable notice describing the personal data and specified purpose before or at the time of requesting consent. Consent is never hidden inside unrelated boilerplate language or obtained through deceptive interface design (dark patterns). All consent requires affirmative, unambiguous user action.</p>

      <h3>4. Withdrawal of Consent</h3>
      <p>You may withdraw consent at any time through the in-app <strong>Privacy Center</strong> (located in Account Settings). In accordance with the DPDP Act, withdrawal is engineered to be as simple and frictionless as giving consent. Withdrawal does not affect processing lawfully completed prior to withdrawal.</p>

      <h3>5. Location Data Architecture & Minimisation</h3>
      <p>KhelDrop requests precise GPS location solely for a <strong>single check-in verification event</strong> when you arrive at a sports venue. KhelDrop does not continuously track you, does not run background <code>watchPosition()</code> surveillance, and does not store your historical movement paths. KhelDrop is not an emergency location provider.</p>

      <h3>6. Third-Party Data Processors</h3>
      <p>KhelDrop contracts with trusted technical infrastructure providers under data-processing covenants: Cloudflare, Inc. (Edge CDN & network security); Supabase / PostgreSQL (Encrypted cloud database); Google LLC (OAuth Single Sign-On); and OpenStreetMap/Nominatim (Reverse geocoding of playground venues). Exact GPS coordinates of users are never shared with OpenStreetMap.</p>

      <h3>7. Data Principal Rights (DPDP Act, Chapter III)</h3>
      <p>You have the following statutory rights, executable free of charge:</p>
      <ul>
        <li><strong>Right to Access (Section 11):</strong> Request a summary of personal data processed and processor details.</li>
        <li><strong>Right to Correction & Erasure (Section 12):</strong> Correct inaccurate data or demand permanent erasure of your account.</li>
        <li><strong>Right to Grievance Redressal (Section 13):</strong> Submit complaints directly to our DPO with guaranteed 7-day resolution.</li>
        <li><strong>Right to Nominate (Section 14):</strong> Nominate an individual to exercise your rights in the event of incapacity or death.</li>
      </ul>

      <h3>8. Account Deletion Protocol</h3>
      <p>When you click "Delete Account" in the Privacy Center, KhelDrop disables access, permanently de-identifies personal profile information, anonymizes display names, clears location records, and marks consent as withdrawn. Security and audit logs required by law are retained for one year pursuant to CERT-In directions and DPDP Rule 3(2).</p>

      <h3>9. Children's Data Policy (Adult-Only 18+ Service)</h3>
      <p>KhelDrop is strictly intended for individuals aged 18 and above. KhelDrop does not knowingly collect or process personal data of children. If we discover an account created by a minor, the account and its personal data will be immediately purged.</p>

      <h3>10. Grievance Redressal & DPO Contact</h3>
      <p>
        <strong>Data Protection Officer:</strong> Ashutosh Yadav<br />
        <strong>Email:</strong> grievance@kheldrop.app | <strong>Privacy Email:</strong> privacy@kheldrop.app<br />
        <strong>Address:</strong> KhelDrop Legal & Regulatory Office, Barakhamba Road, Connaught Place, New Delhi – 110001, India<br />
        <strong>Statutory Timelines:</strong> Acknowledgment within 24 hours | Substantive resolution within 7 calendar days.<br />
        <strong>Escalation:</strong> If dissatisfied, you may lodge an appeal with the <strong>Data Protection Board of India (DPBI)</strong> at <code>https://dpbi.gov.in</code>.
      </p>
    </article>
  );
}

/* =========================================================================================
   DOCUMENT 3: LOCATION PRIVACY NOTICE
   ========================================================================================= */
function LocationPrivacyDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#38bdf8', letterSpacing: '0.06em' }}>
          Geospatial Privacy & Data Minimisation
        </span>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px', letterSpacing: '-0.02em' }}>
          KhelDrop Location Privacy Notice
        </h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>Effective Date: 5 October 2026</div>
      </div>

      <h3>1. Purpose</h3>
      <p>KhelDrop may use location information for: nearby-game discovery; distance calculation; check-in verification; geofencing; and other expressly described location features requested by you.</p>

      <h3>2. Precise Location Minimisation</h3>
      <p>Precise GPS location is collected <strong>only</strong> when a user affirmatively taps "Check In" at a scheduled game. KhelDrop uses this reading strictly to verify arrival within the 200m venue perimeter. KhelDrop does <strong>not</strong> track you continuously, does not keep background location monitors running, and does not record movement tracks.</p>

      <h3>3. Sharing & Visibility</h3>
      <p>KhelDrop will not intentionally expose a user's exact real-time coordinates to other users merely because the user participates in ordinary game discovery or social features. Other players see only the venue name and attendance status.</p>

      <h3>4. Accuracy & Device Limits</h3>
      <p>GPS and mapping information may be inaccurate due to device limitations, environmental obstacles, or satellite availability. KhelDrop does not guarantee exact coordinates or venue boundaries. Location data must never be relied upon for emergency or medical decisions.</p>

      <h3>5. Location Permission Controls</h3>
      <p>You can manage browser or device location permissions at any time through your operating system or browser settings. In addition, you can control your preferred radar radius and clear cached coordinates directly in KhelDrop Settings.</p>
    </article>
  );
}

/* =========================================================================================
   DOCUMENT 4: COMMUNITY & ACCEPTABLE USE POLICY
   ========================================================================================= */
function CommunityGuidelinesDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#f59e0b', letterSpacing: '0.06em' }}>
          Fair Play & Platform Standards
        </span>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px', letterSpacing: '-0.02em' }}>
          KhelDrop Community & Acceptable Use Policy
        </h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>Effective Date: 5 October 2026</div>
      </div>

      <p>KhelDrop is intended to be a respectful, reliable, and safe platform for recreational sporting activities.</p>

      <h3>1. Respect Other Users</h3>
      <p>Do not: harass; threaten; stalk; intimidate; bully; repeatedly contact a person who has asked you to stop; engage in discriminatory abuse; or use sexual or threatening conduct to intimidate another person on or off the field.</p>

      <h3>2. No Impersonation or Fraud</h3>
      <p>Do not: impersonate another user; create fake identities for deception; create fraudulent games; manipulate game information; misrepresent your qualifications or skills; or use another person's account.</p>

      <h3>3. Privacy & Personal Information</h3>
      <p>Do not: publish another person's address; expose private communications; obtain another person's credentials; disclose private photographs without authority; track another person without lawful authority; or use KhelDrop to facilitate stalking.</p>

      <h3>4. Illegal Activity & Real-Money Gaming Prohibition</h3>
      <p>KhelDrop must not be used to facilitate criminal activity, unlawful financial activity, betting, wagering, gambling or prohibited money-game activity. Recreational sports must remain zero-cost and free of betting pools.</p>

      <h3>5. Malware and Technical Abuse</h3>
      <p>Do not: attack KhelDrop infrastructure; attempt unauthorised access; introduce malicious code; bypass authentication; abuse APIs; interfere with another user's access; or scrape information in violation of applicable law or these Terms.</p>

      <h3>6. Content Standards</h3>
      <p>User content must not: violate law; infringe copyright or trademark rights; contain malware; impersonate others; expose private information; contain abusive or harassing material; or mislead users about the nature of a game.</p>

      <h3>7. Enforcement</h3>
      <p>KhelDrop may: warn a user; restrict content; remove content; suspend an account; terminate an account; cancel a game; preserve evidence; and refer matters to appropriate law-enforcement authorities where required.</p>
    </article>
  );
}

/* =========================================================================================
   DOCUMENT 5: SAFETY GUIDELINES
   ========================================================================================= */
function SafetyGuidelinesDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#ef4444', letterSpacing: '0.06em' }}>
          Real-World Sports Precaution
        </span>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px', letterSpacing: '-0.02em' }}>
          KhelDrop Safety Guidelines
        </h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>Effective Date: 5 October 2026</div>
      </div>

      <h3>1. KhelDrop Connects Real People</h3>
      <p>KhelDrop helps players find and participate in real-world sporting activities. Treat every new interaction with appropriate caution and common sense.</p>

      <h3>2. Meeting Other Users</h3>
      <p>Where possible: meet at recognised, public sporting venues; inform a friend or family member where you are playing; arrange your own transportation; avoid sharing sensitive personal information; and leave immediately if you feel unsafe or uncomfortable.</p>

      <h3>3. No Background-Check Representation</h3>
      <p>Unless expressly stated otherwise, KhelDrop does not perform criminal background checks or comprehensive identity audits on every user. Always exercise personal discretion.</p>

      <h3>4. Sport & Physical Safety</h3>
      <p>You are responsible for: determining your own medical readiness to play; bringing appropriate athletic footwear and protective gear; following venue safety rules; staying hydrated; and pausing or stopping participation if you feel fatigued, dizzy, or injured.</p>

      <h3>5. Emergencies</h3>
      <p>KhelDrop is <strong>not</strong> an emergency dispatch service. For immediate physical danger, medical emergencies, or criminal conduct, contact local emergency services (112 in India) or police immediately.</p>

      <h3>6. Report & Block Controls</h3>
      <p>Every match and user profile includes [Report] and [Block] controls. You can instantly report misconduct or block another player from messaging you or viewing your match attendance.</p>
    </article>
  );
}

/* =========================================================================================
   DOCUMENT: STATUTORY AGE LIMITATION & MINOR PROTECTION POLICY (DPDP ACT §9 COMPLIANCE)
   ========================================================================================= */
function AgeLimitationPolicyDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#ec4899', letterSpacing: '0.06em' }}>
          DPDP Act §9 & IT Rules Statutory Directive
        </span>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px', letterSpacing: '-0.02em' }}>
          KhelDrop Age Limitation & Minor Protection Policy
        </h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>
          Effective Date: 5 October 2026 | Version 2026.1 | Compliance Lead: Ashutosh Yadav (DPO)
        </div>
      </div>

      <div style={{ background: 'rgba(236, 72, 153, 0.08)', border: '1px solid rgba(236, 72, 153, 0.25)', padding: '16px 20px', borderRadius: '12px', marginBottom: '28px' }}>
        <p style={{ margin: 0, fontSize: '13px', color: '#f472b6', fontWeight: 600 }}>
          Statutory Statement: KhelDrop is strictly an adult-only (18+) recreational sports platform. In strict adherence to Section 9 of the Digital Personal Data Protection Act, 2023 ("DPDP Act") and Section 11 of the Indian Contract Act, 1872, individuals under eighteen (18) years of age are legally prohibited from registering, creating profiles, joining matches, reserving turf lobbies, or participating in KhelDrop communications.
        </p>
      </div>

      <h3>1. Legislative Mandate and Definitional Scope</h3>
      <p>
        Under <strong>Section 2(f) and Section 9 of the DPDP Act, 2023</strong>, a "child" is defined as any individual who has not completed the age of eighteen (18) years. 
        Under <strong>Section 11 of the Indian Contract Act, 1872</strong>, a minor is incompetent to contract, rendering any electronic agreement entered into by a minor void <em>ab initio</em> (*Mohori Bibee v. Dharmodas Ghose*). 
        To eliminate any risk of unlawful minor data processing, psychological harm, or unsanctioned physical exposure, KhelDrop operates an uncompromising <strong>Zero-Minor Access Model</strong>.
      </p>

      <h3>2. Absolute Ban on Child Data Processing & Tracking</h3>
      <p>
        Pursuant to <strong>Section 9(3) of the DPDP Act</strong>:
      </p>
      <ul>
        <li>KhelDrop does not knowingly collect, store, index, or process personal data of children.</li>
        <li>KhelDrop enforces a total statutory prohibition against tracking, behavioral monitoring, or profiling of children.</li>
        <li>KhelDrop conducts no targeted advertising or gamified nudges directed at minors.</li>
        <li>KhelDrop does not permit parents or legal guardians to open or delegate secondary accounts for minors on this platform.</li>
      </ul>

      <h3>3. Technical Age-Gating Architecture</h3>
      <p>KhelDrop enforces a multi-layer programmatic age-verification barrier:</p>
      <ul>
        <li><strong>Email Registration Gate:</strong> Requires mandatory Date of Birth entry. Dynamic server and client verification rejects any applicant with a calculated age under 18 years.</li>
        <li><strong>Third-Party Single Sign-On (Google OAuth) Gate:</strong> Users authenticating via Google or external federated ID systems are automatically routed to a mandatory post-login Age Affirmation barrier where DOB and an express legal declaration must be provided before matchmaking or profile features unlock.</li>
        <li><strong>Affirmative Legal Representation:</strong> All users must execute a legally binding clickwrap declaration affirming they are at least 18 years old and competent to contract.</li>
      </ul>

      <h3>4. Fraudulent Misrepresentation and Legal Penalties</h3>
      <p>
        Any minor, or any person acting on behalf of a minor, who provides a falsified date of birth, uses forged credentials, or misrepresents their age to gain access to KhelDrop:
      </p>
      <ul>
        <li>Directly violates the KhelDrop Terms of Service and Community Guidelines.</li>
        <li>Commits cheating by personation under <strong>Section 319 of the Bharatiya Nyaya Sanhita, 2023 (BNS)</strong>.</li>
        <li>Commits identity fraud under <strong>Section 66C and Section 66D of the Information Technology Act, 2000</strong>.</li>
      </ul>

      <h3>5. Detection and Immediate Data Purge Protocol</h3>
      <p>
        If KhelDrop discovers or has reasonable grounds to suspect that an existing account belongs to a minor:
      </p>
      <ul>
        <li>The account is immediately locked and suspended from matchmaking and messaging.</li>
        <li>Pursuant to <strong>Section 8(7) and Section 9 of the DPDP Act</strong>, all personal data, profile photographs, geocache telemetry, and chat records linked to the account are permanently and irreversibly purged from active systems within 24 hours.</li>
        <li>Where required by law, compliance logs documenting the purge are retained strictly for regulatory auditing under CERT-In and DPBI directives.</li>
      </ul>

      <h3>6. Reporting Underage Accounts (Expedited Channel)</h3>
      <p>
        If you are a parent, guardian, educator, or player who believes an individual under 18 has registered an account on KhelDrop, please report it immediately:
      </p>
      <ul>
        <li><strong>Dedicated Child Safety Email:</strong> <a href="mailto:child-safety@kheldrop.app" style={{ color: '#ec4899', fontWeight: 600 }}>child-safety@kheldrop.app</a></li>
        <li><strong>Grievance Officer:</strong> Ashutosh Yadav, DPO (<a href="mailto:grievance@kheldrop.app" style={{ color: '#a78bfa', fontWeight: 600 }}>grievance@kheldrop.app</a>)</li>
        <li><strong>In-App Report:</strong> Tap the <strong>[Report User]</strong> button on any profile and select <em>"Underage User / Minor (&lt;18)"</em> for priority automated review.</li>
      </ul>
      <p>
        All reports involving suspected minors are treated as priority statutory escalations and resolved within <strong>24 hours</strong>.
      </p>
    </article>
  );
}

/* =========================================================================================
   DOCUMENT 6: NO-PAYMENT NOTICE
   ========================================================================================= */
function NoPaymentDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#10b981', letterSpacing: '0.06em' }}>
          Zero-Cost Platform Architecture
        </span>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px', letterSpacing: '-0.02em' }}>
          KhelDrop No-Payment Notice
        </h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>Effective Date: 5 October 2026</div>
      </div>

      <div style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.25)', padding: '16px 20px', borderRadius: '12px', marginBottom: '24px' }}>
        <p style={{ margin: 0, fontSize: '13.5px', color: '#6ee7b7', fontWeight: 600 }}>
          As of 5 October 2026, KhelDrop is a completely free, zero-cost recreational sports platform.
        </p>
      </div>

      <p>KhelDrop does <strong>not</strong> currently provide or facilitate: payment processing; digital wallets; paid match entry fees; recurring subscriptions; betting; wagering; or monetary prizes.</p>
      <p>Users must not use KhelDrop to arrange unauthorized commercial transactions, side-bets, or money-game pools. Any future monetization or paid booking features will undergo exhaustive regulatory, consumer, and tax review prior to deployment.</p>
    </article>
  );
}

/* =========================================================================================
   DOCUMENT 7: GRIEVANCE REDRESSAL POLICY (STATUTORY IT RULES & DPDP)
   ========================================================================================= */
function GrievancePolicyDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#c084fc', letterSpacing: '0.06em' }}>
          Statutory Compliance Mechanism
        </span>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px', letterSpacing: '-0.02em' }}>
          KhelDrop Grievance Redressal Policy
        </h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>
          In Compliance with Rule 3(2) of IT Rules, 2021/2026 & Section 13 of DPDP Act, 2023
        </div>
      </div>

      <div style={{ background: 'rgba(192, 132, 252, 0.08)', border: '1px solid rgba(192, 132, 252, 0.25)', padding: '18px 20px', borderRadius: '12px', marginBottom: '28px' }}>
        <h4 style={{ margin: '0 0 8px', color: '#e9d5ff', fontSize: '15px' }}>Designated Grievance & Data Protection Officer</h4>
        <p style={{ margin: '0 0 6px', fontSize: '13px' }}><strong>Officer Name:</strong> Ashutosh Yadav</p>
        <p style={{ margin: '0 0 6px', fontSize: '13px' }}><strong>Email:</strong> <a href="mailto:grievance@kheldrop.app" style={{ color: '#c084fc', textDecoration: 'underline' }}>grievance@kheldrop.app</a></p>
        <p style={{ margin: '0 0 6px', fontSize: '13px' }}><strong>Office Address:</strong> KhelDrop Legal & Regulatory Cell, Barakhamba Road, Connaught Place, New Delhi – 110001, India</p>
        <p style={{ margin: 0, fontSize: '12.5px', color: '#a1a1aa' }}><strong>Working Hours:</strong> Monday to Friday, 09:30 AM to 06:30 PM IST</p>
      </div>

      <h3>1. Scope of Grievances</h3>
      <p>Users and affected persons may submit formal complaints regarding: privacy and personal-data processing; online harassment or stalking; unsafe or offensive user conduct; unlawful or defamatory content; impersonation or fraudulent accounts; copyright or intellectual property infringement; and arbitrary account actions.</p>

      <h3>2. How to File a Grievance</h3>
      <p>Grievances may be filed through the in-app <strong>Privacy Center</strong> or via email to <code>grievance@kheldrop.app</code>. Every valid grievance automatically generates a unique tracking number in the format:</p>
      <div style={{ background: 'rgba(0,0,0,0.4)', padding: '10px 16px', borderRadius: '8px', fontFamily: 'monospace', color: '#38bdf8', fontSize: '14px', width: 'fit-content' }}>
        PD-GRV-[YEAR]-[NUMBER] (e.g., PD-GRV-2026-1042)
      </div>

      <h3>3. Investigation Procedure</h3>
      <p>Upon receipt, the Grievance Officer reviews account records, reports, relevant chat messages (where legally permitted), and audit trails. Actions may include content takedowns, user warnings, temporary suspensions, or permanent bans.</p>

      <h3>4. Mandatory Statutory Timelines</h3>
      <ul>
        <li><strong>Receipt Acknowledgment:</strong> Generated within <strong>24 hours</strong> of complaint filing.</li>
        <li><strong>Substantive Resolution:</strong> Concluded within <strong>7 calendar days</strong> from the date of receipt.</li>
      </ul>

      <h3>5. Statutory Escalation to DPBI</h3>
      <p>If you are not satisfied with the resolution or if KhelDrop fails to respond within the statutory timeframe, you have the right under Section 13(3) of the DPDP Act, 2023 to escalate your complaint to the <strong>Data Protection Board of India (DPBI)</strong> via <code>https://dpbi.gov.in</code>.</p>
    </article>
  );
}

/* =========================================================================================
   DOCUMENT 8: COPYRIGHT & INTELLECTUAL PROPERTY POLICY
   ========================================================================================= */
function CopyrightPolicyDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#e879f9', letterSpacing: '0.06em' }}>
          Notice & Takedown Procedure
        </span>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px', letterSpacing: '-0.02em' }}>
          KhelDrop Copyright & Intellectual Property Policy
        </h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>Effective Date: 5 October 2026</div>
      </div>

      <h3>1. Respect for Intellectual Property</h3>
      <p>KhelDrop respects the intellectual property rights of creators and athletes. Users may only post images, venue photographs, or content that they own or have explicit authorization to use.</p>

      <h3>2. Filing an Infringement Notice</h3>
      <p>To submit a notice of claimed infringement, provide our designated agent with: your full contact details; description of the copyrighted work; URL or precise location of the infringing material; a statement of good-faith belief; and a statement under penalty of perjury that you are the copyright holder or authorized representative.</p>
      <p>Email notices to: <strong>copyright@kheldrop.app</strong></p>

      <h3>3. Counter-Notification</h3>
      <p>If you believe your content was disabled or removed by mistake or misidentification, you may submit a counter-notification explaining why the material should be restored, in accordance with applicable law.</p>
    </article>
  );
}

/* =========================================================================================
   DOCUMENT 9: USER CONTENT NOTICE
   ========================================================================================= */
function UserContentDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#93c5fd', letterSpacing: '0.06em' }}>
          Ownership & License Grants
        </span>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px', letterSpacing: '-0.02em' }}>
          KhelDrop User Content Notice
        </h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>Effective Date: 5 October 2026</div>
      </div>

      <p>Users retain full ownership of intellectual-property rights they lawfully hold in User Content submitted to KhelDrop (such as game descriptions, avatar photos, and team names).</p>
      <p>By submitting User Content, you grant KhelDrop a limited, worldwide, royalty-free license solely to host, store, technically format, transmit, and display the content to intended participants as necessary to provide the matchmaking service.</p>
      <p>KhelDrop does <strong>not</strong> acquire ownership of your content. You can remove your content at any time by editing your profile or deleting your game posting.</p>
    </article>
  );
}

/* =========================================================================================
   DOCUMENT 10: DATA PROCESSING AGREEMENT (DPA) SUMMARY
   ========================================================================================= */
function DpaDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#38bdf8', letterSpacing: '0.06em' }}>
          Processor Obligations & Sub-processor Framework
        </span>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px', letterSpacing: '-0.02em' }}>
          KhelDrop Data Processing Agreement (DPA)
        </h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>Effective Date: 5 October 2026</div>
      </div>

      <p>This Data Processing Agreement establishes the statutory covenants governing technical processors engaged by KhelDrop Technologies Private Limited pursuant to the DPDP Act, 2023.</p>
      <p>Under Section 8(1) of the DPDP Act, KhelDrop as Data Fiduciary remains directly accountable to Data Principals for processing conducted on its behalf by sub-processors.</p>
      <p>All contracted sub-processors (including Cloudflare and Supabase) are bound to: process data strictly upon documented instructions; maintain state-of-the-art cryptographic safeguards; notify KhelDrop of any personal data breach without delay; and assist in satisfying Data Principal rights requests.</p>
    </article>
  );
}

/* =========================================================================================
   DOCUMENT 11: SECURITY ARCHITECTURE & CERT-In COMPLIANCE
   ========================================================================================= */
function SecurityDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#10b981', letterSpacing: '0.06em' }}>
          Security Safeguards & CERT-In Directives
        </span>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px', letterSpacing: '-0.02em' }}>
          KhelDrop Security Architecture Policy
        </h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>In Compliance with CERT-In Section 70B & DPDP Rules, 2025</div>
      </div>

      <h3>1. Security Controls Implemented</h3>
      <ul>
        <li><strong>No Client-Side Secrets:</strong> Administrative keys and secrets are strictly enforced on backend microservices; zero secrets in frontend code.</li>
        <li><strong>Server-Side Role-Based Authorization:</strong> Every administrative and creator endpoint enforces cryptographic JWT inspection.</li>
        <li><strong>Data-At-Rest & Transit Encryption:</strong> All communications leverage TLS 1.3; database volumes are encrypted using AES-256.</li>
        <li><strong>Rate Limiting & DDoS Shielding:</strong> Edge-level rate limiting prevents brute force and credential stuffing attacks.</li>
      </ul>

      <h3>2. CERT-In Incident Escalation (6-Hour Window)</h3>
      <p>Under the CERT-In Directions issued pursuant to Section 70B of the Information Technology Act, 2000, KhelDrop maintains an incident response runbook requiring notification of covered cybersecurity incidents to CERT-In within <strong>six (6) hours</strong> of detection.</p>
      <p>System logs and audit trails are preserved within secure Indian storage for a minimum duration of <strong>one (1) year</strong>.</p>
    </article>
  );
}

/* =========================================================================================
   DOCUMENT 12: COOKIE POLICY
   ========================================================================================= */
function CookiePolicyDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#f59e0b', letterSpacing: '0.06em' }}>
          Local Storage & Cookies
        </span>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px', letterSpacing: '-0.02em' }}>
          KhelDrop Cookie Policy
        </h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>Effective Date: 5 October 2026</div>
      </div>

      <p>KhelDrop uses cookies and browser local storage strictly for essential session authentication, security tokens, and user preferences (such as radar radius and sport filters). KhelDrop does <strong>not</strong> deploy third-party advertising trackers or behavioral profiling pixels.</p>
      <p>You can customize your preferences anytime in the <Link to="/cookie-preferences" style={{ color: '#a78bfa', textDecoration: 'underline' }}>Cookie Preferences</Link> tab.</p>
    </article>
  );
}

/* =========================================================================================
   DOCUMENT 13: COOKIE PREFERENCES (INTERACTIVE TAB)
   ========================================================================================= */
function CookiePreferencesDoc({ settings, onToggle, onSave, savedStatus }: any) {
  return (
    <div>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', color: '#8b5cf6', letterSpacing: '0.06em' }}>
          Data Privacy Controls
        </span>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px', letterSpacing: '-0.02em' }}>
          Cookie & Storage Preferences
        </h1>
        <p style={{ margin: '4px 0 0', color: '#a1a1aa', fontSize: '13.5px' }}>
          Manage which categories of local storage and cookies are active on your device.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ padding: '16px 20px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '14px', color: '#fff' }}>Strictly Essential Storage (Required)</div>
            <div style={{ fontSize: '12px', color: '#71717a', marginTop: '2px' }}>Authentication session tokens, CSRF protection, and security verification.</div>
          </div>
          <input type="checkbox" checked disabled style={{ width: '18px', height: '18px', accentColor: '#8b5cf6' }} />
        </div>

        <div style={{ padding: '16px 20px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '14px', color: '#fff' }}>Sport Radar & Match Preferences</div>
            <div style={{ fontSize: '12px', color: '#71717a', marginTop: '2px' }}>Remembers your chosen radius (e.g. 5km), favorite sports, and map zoom.</div>
          </div>
          <input type="checkbox" checked={settings.functional} onChange={() => onToggle('functional')} style={{ width: '18px', height: '18px', accentColor: '#8b5cf6', cursor: 'pointer' }} />
        </div>

        <div style={{ padding: '16px 20px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '14px', color: '#fff' }}>Radar Telemetry & Cache</div>
            <div style={{ fontSize: '12px', color: '#71717a', marginTop: '2px' }}>Locally caches nearby turf names to accelerate repeat map visits.</div>
          </div>
          <input type="checkbox" checked={settings.matchmaking} onChange={() => onToggle('matchmaking')} style={{ width: '18px', height: '18px', accentColor: '#8b5cf6', cursor: 'pointer' }} />
        </div>

        <button
          onClick={onSave}
          style={{
            marginTop: '12px',
            alignSelf: 'flex-start',
            padding: '10px 24px',
            borderRadius: '999px',
            background: '#8b5cf6',
            color: '#fff',
            border: 'none',
            fontSize: '13px',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(139, 92, 246, 0.4)',
          }}
        >
          {savedStatus ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <CheckCircle size={14} style={{ flexShrink: 0 }} /> Preferences Saved
            </span>
          ) : (
            'Save Preferences'
          )}
        </button>
      </div>
    </div>
  );
}

/* =========================================================================================
   ADDITIONAL COMPLIANCE DOCS (DISCLAIMER, ACCESSIBILITY, HELP)
   ========================================================================================= */
function DisclaimerDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px' }}>Platform Disclaimer</h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>Effective Date: 5 October 2026</div>
      </div>
      <p>KhelDrop is an electronic communications intermediary connecting sports players. KhelDrop does not own venues, does not employ coaches or referees, and disclaims all liability for turf conditions, weather cancellations, or player conduct to the maximum extent permitted by Indian law.</p>
    </article>
  );
}

function AccessibilityDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px' }}>Accessibility Statement</h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>Commitment to Inclusive Access</div>
      </div>
      <p>KhelDrop strives to conform with the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards, ensuring accessible contrast, keyboard navigability, and screen-reader semantics.</p>
    </article>
  );
}

function HelpDoc() {
  return (
    <article style={{ lineHeight: 1.7, color: '#d4d4d8', fontSize: '14px' }}>
      <div style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '20px', marginBottom: '28px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#ffffff', margin: '8px 0 4px' }}>Help Center & Support</h1>
        <div style={{ fontSize: '12.5px', color: '#71717a' }}>Official Support Channels</div>
      </div>
      <p>Need assistance with a match, reliability score dispute, or account access? Contact us directly:</p>
      <ul>
        <li><strong>General Support:</strong> <a href="mailto:support@kheldrop.app" style={{ color: '#a78bfa' }}>support@kheldrop.app</a></li>
        <li><strong>Privacy & Data Rights:</strong> <a href="mailto:privacy@kheldrop.app" style={{ color: '#a78bfa' }}>privacy@kheldrop.app</a></li>
        <li><strong>Statutory Grievances:</strong> <a href="mailto:grievance@kheldrop.app" style={{ color: '#a78bfa' }}>grievance@kheldrop.app</a></li>
      </ul>
    </article>
  );
}
